import type { FC, PropsWithChildren } from 'react'

import styled from '@emotion/styled'

import sanitizeHtml from 'sanitize-html'

import { useTheme } from '@emotion/react'

import ImageComponent from '@segment/matcha/components/Image'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import Button from '@segment/matcha/components/Button'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import PropCardBase, * as PropCard from '@segment/matcha/recipes/PropCard'

import useMatchMedia from '@segment/utils/useMatchMedia'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import hipaaCompliantBadgePng2x from './assets/HipaaCompliantBadge@2x.png'
import hipaaCompliantBadgeWebp from './assets/HipaaCompliantBadge.webp'
import hipaaCompliantBadgeWebp2x from './assets/HipaaCompliantBadge@2x.webp'

import iso27001BadgePng2x from './assets/Iso27001Badge@2x.png'
import iso27001BadgeWebp from './assets/Iso27001Badge.webp'
import iso27001BadgeWebp2x from './assets/Iso27001Badge@2x.webp'

import iso27017BadgePng2x from './assets/Iso27017Badge@2x.png'
import iso27017BadgeWebp from './assets/Iso27017Badge.webp'
import iso27017BadgeWebp2x from './assets/Iso27017Badge@2x.webp'

import iso27018BadgePng2x from './assets/Iso27018Badge@2x.png'
import iso27018BadgeWebp from './assets/Iso27018Badge.webp'
import iso27018BadgeWebp2x from './assets/Iso27018Badge@2x.webp'

import aicpaSocBadgePng2x from './assets/AicpaSocBadge@2x.png'
import aicpaSocBadgeWebp from './assets/AicpaSocBadge.webp'
import aicpaSocBadgeWebp2x from './assets/AicpaSocBadge@2x.webp'

import csaBadgePng2x from './assets/CsaBadge@2x.png'
import csaBadgeWebp from './assets/CsaBadge.webp'
import csaBadgeWebp2x from './assets/CsaBadge@2x.webp'

import cardImagePng2x from './assets/DataDrivenReport@2x.png'
import cardImageWebp from './assets/DataDrivenReport.webp'
import cardImageWebp2x from './assets/DataDrivenReport@2x.webp'

const copy = {
  overline: `Security Highlight`,
  heading: `Industry-leading Security & Compliance`,
  description: `Twilio Segment is HIPAA-eligible and able to receive, maintain, and transmit PHI on behalf of our customers in the healthcare industry. Segment is able to sign BAAs for managing PHI, or PII that should be treated as PHI.`,
  learnMore: `Learn more about how <a href="https://twil.io/architecting-for-hipaa/">to architect compliantly with Segment</a>.`,
}

const link = {
  text: `Explore the Privacy Portal`,
  url: `/product/privacy-portal/`,
}

const card = {
  heading: `Data-Driven Strategies for Growth: A Guide for Healthcare Businesses`,
  description: `In this guide we cover why healthcare leaders should consider a customer data platform (CDP) to create better patient interactions across the care journey.`,
  link: {
    text: `Access the guide`,
    url: `https://learn.segment.com/cdp-for-healthcare/`,
  },
}

const IndustryLeadingSecurity: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  // Mobile breakpoints
  const theme = useTheme()
  const mobileResolutionMediaQuery = theme.breakpoints.down(`md`, false)
  const isSmallResolution = useMatchMedia(mobileResolutionMediaQuery, true)

  return (
    <Wrapper>
      <FirstSectionBase variant="centered">
        <Section.Copy>
          <StyledCopyText centered={true}>
            <CopyText.Overline>{copy.overline}</CopyText.Overline>
            <CopyText.Heading>{copy.heading}</CopyText.Heading>
            <CopyText.Description>{copy.description}</CopyText.Description>
            <CopyText.Link href={getAbsoluteUrl(link.url, pageConfig)}>{link.text}</CopyText.Link>
          </StyledCopyText>
        </Section.Copy>
      </FirstSectionBase>
      <SecondSectionBase variant="noCopy">
        <Section.Contents>
          <StyledColumnTrack columns={1} overrides={{ xs: 2, lg: 3 }}>
            <Image
              alt="ISO 27001"
              images={{
                webp: iso27001BadgeWebp,
                webp2x: iso27001BadgeWebp2x,
                png2x: iso27001BadgePng2x,
              }}
              size={[120, 140]}
            />
            <Image
              alt="ISO 27017"
              images={{
                webp: iso27017BadgeWebp,
                webp2x: iso27017BadgeWebp2x,
                png2x: iso27017BadgePng2x,
              }}
              size={[120, 140]}
            />
            <Image
              alt="ISO 27018"
              images={{
                webp: iso27018BadgeWebp,
                webp2x: iso27018BadgeWebp2x,
                png2x: iso27018BadgePng2x,
              }}
              size={[120, 140]}
            />
            <Image
              alt="Cloud Security Alliance"
              images={{
                webp: csaBadgeWebp,
                webp2x: csaBadgeWebp2x,
                png2x: csaBadgePng2x,
              }}
              size={[180, 64]}
            />
            <Image
              alt="AICPA SOC"
              images={{
                webp: aicpaSocBadgeWebp,
                webp2x: aicpaSocBadgeWebp2x,
                png2x: aicpaSocBadgePng2x,
              }}
              size={[96, 96]}
            />
            <Image
              alt="HIPAA Compliant"
              images={{
                webp: hipaaCompliantBadgeWebp,
                webp2x: hipaaCompliantBadgeWebp2x,
                png2x: hipaaCompliantBadgePng2x,
              }}
              size={[180, 96]}
            />
          </StyledColumnTrack>
          <CopyTextDescription
            tag="div"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(copy.learnMore, { allowedTags: [`a`] }),
            }}
          />
        </Section.Contents>
      </SecondSectionBase>
      <ThirdSectionBase variant="centered">
        <StyledPropCardBase reverse={isSmallResolution}>
          <PropCard.Copy>
            <PropCard.Title>{card.heading}</PropCard.Title>
            <PropCard.Text>{card.description}</PropCard.Text>
            <Button color="primary" href={card.link.url} size="large" tag="a" variant="filled">
              {card.link.text}
            </Button>
          </PropCard.Copy>
          <PropCard.Img>
            <Image
              alt={card.heading}
              images={{
                webp: cardImageWebp,
                webp2x: cardImageWebp2x,
                png2x: cardImagePng2x,
              }}
              size={[312, 288]}
            />
          </PropCard.Img>
        </StyledPropCardBase>
      </ThirdSectionBase>
    </Wrapper>
  )
}

interface ImageProps {
  images: {
    png2x: string
    webp: string
    webp2x: string
  }
  alt: string
  size?: readonly [number, number]
}

const Image: FC<PropsWithChildren<ImageProps>> = ({ images, alt, size = [179, 120] }) => {
  const [w, h] = size
  return (
    <ImageWrapper>
      <picture>
        <source type="image/webp" srcSet={`${images.webp}, ${images.webp2x} 2x`} />
        <ImageComponent width={w} height={h} src={images.png2x} alt={alt} loading="lazy" />
      </picture>
    </ImageWrapper>
  )
}

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`

const Wrapper = styled.div`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    max-width: 720px;
    margin: auto;
  }
`

const StyledPropCardBase = styled(PropCardBase)`
  max-width: 800px;
  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    padding: 56px 0 56px 56px;
  }
`

const FirstSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    ${({ theme }) => theme.breakpoints.up(`md`)} {
      padding-left: 0;
      padding-right: 0;
    }
    row-gap: 0;
  }
`

const SecondSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 0;
    padding-bottom: 40px;
  }
`

const ThirdSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    ${({ theme }) => theme.breakpoints.down(`md`)} {
      padding-top: 0;
      padding-bottom: 0;
    }
  }
`

const StyledCopyText = styled(CopyTextBase)`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    text-align: left;
  }
`

const StyledColumnTrack = styled(ColumnTrack)`
  row-gap: 40px;
  column-gap: 40px;
  max-width: 616px;
  margin: 0 auto;
`

const CopyTextDescription = styled(CopyText.Description)`
  padding-top: 74px;
  text-align: center;
`

export default IndustryLeadingSecurity
